import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, Check } from "lucide-react";
import JTLogo from "../components/JTLogo";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APPLICATIONS_API =
  process.env.REACT_APP_APPLICATIONS_API_URL ||
  `${process.env.REACT_APP_BACKEND_URL}/api/applications`;

const REFERRAL_OPTIONS = [
  { label: "Charter Member", value: "Charter Member" },
  { label: "BFUSA", value: "bfusa" },
  { label: "Instagram", value: "Instagram" },
  { label: "TikTok", value: "tiktok" },
  { label: "Facebook", value: "Facebook" },
  { label: "Friend", value: "Friend" },
  { label: "Other", value: "Other" },
];

const CALL_METHODS = [
  { label: "FaceTime",        value: "facetime",    placeholder: "Phone number or Apple Account email" },
  { label: "Instagram Video", value: "instagram",   placeholder: "Instagram @username" },
  { label: "WhatsApp",        value: "whatsapp",    placeholder: "WhatsApp phone number" },
  { label: "Google Meet",     value: "google_meet", placeholder: "Email address for Google Meet" },
  { label: "Other",           value: "other",       placeholder: "Enter the contact information and method we should use" },
];

const CHARTER_CITIES = [
  "Atlanta", "Boston", "Detroit", "Houston", "Nashville",
  "New York City", "Philadelphia", "Salt Lake City", "San Francisco",
];

export default function Apply() {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    airline: "",
    base_airport: "",
    years_flying: "",
    instagram_handle: "",
    favorite_layover_city: "",
    lounge_doors_response: "",
    referral_source: "",
    invite_code: "",
    preferred_call_method: "",
    verification_call_contact: "",
    verification_call_availability: "",
  });

  // Auto-populate invite code from ?ref= query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      const code = ref.trim().toUpperCase().slice(0, 32);
      setForm((f) => (f.invite_code ? f : { ...f, invite_code: code }));
    }
  }, [location.search]);

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };
  const setReferral = (val) => setForm((f) => ({ ...f, referral_source: val }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Required-field validation
    const required = ["first_name", "last_name", "email", "airline", "base_airport", "years_flying"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        setError("Please complete every required field above.");
        return;
      }
    }

    // Verification video-call fields
    if (!form.preferred_call_method) {
      setError("Please select a preferred video call method above.");
      return;
    }
    if (!String(form.verification_call_contact || "").trim()) {
      setError("Please provide the contact information for your verification call.");
      return;
    }

    setSubmitting(true);
    // Diagnostic: confirm endpoint at submit time so it's visible in browser devtools
    // eslint-disable-next-line no-console
    console.info("[apply] POST →", APPLICATIONS_API);

    try {
      const res = await axios.post(APPLICATIONS_API, {
        ...form,
        years_flying: parseInt(form.years_flying, 10) || 0,
      });
      // eslint-disable-next-line no-console
      console.info("[apply] success", res.status, res.data);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // Full structured logging for debugging
      // eslint-disable-next-line no-console
      console.error("[apply] submit failed", {
        endpoint: APPLICATIONS_API,
        message: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        code: err?.code,
      });

      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (!err?.response) {
        // No response = network failure, DNS, or CORS preflight rejection
        if (err?.message?.toLowerCase().includes("network")) {
          setError(
            `Network error — could not reach ${APPLICATIONS_API}. ` +
            `This is usually a CORS block or the endpoint is unreachable. (${err.message})`
          );
        } else {
          setError(`Request failed before reaching the server: ${err?.message || "unknown"}`);
        }
      } else if (status === 409) {
        setError("This email has already submitted a request.");
      } else if (status === 404) {
        setError(
          `404 Not Found — the submission endpoint does not exist at ${APPLICATIONS_API}. ` +
          `Make sure POST /api/applications is deployed on the main app backend.`
        );
      } else if (status === 422 || status === 400) {
        const validationMsg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => `${(d.loc || []).join('.')}: ${d.msg}`).join(" · ")
              : "Validation error — please check the form.";
        setError(`${status}: ${validationMsg}`);
      } else {
        setError(
          `${status} ${err.response.statusText || ""} — ${
            typeof detail === "string" ? detail : "submission failed."
          }`.trim()
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="App" data-testid="apply-success-page">
        <SiteHeader />
        <section className="min-h-[100vh] flex flex-col items-center justify-center px-6 lounge-vignette">
          <div className="text-center max-w-[680px] fade-up">
            <JTLogo size={92} />
            <div
              className="font-label mt-10"
              style={{ color: "var(--jt-gold)", fontSize: "11px" }}
            >
              APPLICATION RECEIVED
            </div>
            <h1
              className="font-display mt-6"
              style={{
                fontSize: "clamp(2.2rem, 4.5vw, 3.8rem)",
                color: "var(--jt-ivory)",
                lineHeight: 1.08,
                fontWeight: 500,
                maxWidth: "18ch",
                margin: "24px auto 0",
              }}
              data-testid="apply-success-headline"
            >
              The lounge has your name.
            </h1>
            <div className="gold-divider mx-auto mt-10" />
            <p
              className="font-editorial italic mt-10"
              style={{
                fontSize: "clamp(1.1rem, 1.8vw, 1.5rem)",
                color: "var(--jt-bone)",
                lineHeight: 1.55,
                maxWidth: "44ch",
                margin: "32px auto 0",
              }}
            >
              Every submission is personally reviewed. If you are selected, you will hear from us privately.
            </p>
            <button
              onClick={() => navigate("/")}
              className="jt-cta-ghost mt-14"
              data-testid="apply-success-back"
            >
              Return Home
            </button>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="App" data-testid="apply-page">
      <SiteHeader />

      <section className="pt-32 pb-20 px-6 lounge-vignette grain relative">
        <div className="max-w-[760px] mx-auto text-center">
          <JTLogo size={84} />

          <div
            className="font-label mt-12 flex flex-wrap items-center justify-center gap-4"
            style={{ fontSize: "11px" }}
          >
            <span style={{ color: "var(--jt-gold)" }}>ARRIVE</span>
            <span style={{ color: "var(--jt-gold)" }}>·</span>
            <span style={{ color: "var(--jt-gold)" }}>CONNECT</span>
            <span style={{ color: "var(--jt-gold)" }}>·</span>
            <span style={{ color: "var(--jt-gold)" }}>REPEAT</span>
          </div>

          <h1
            className="font-display mt-10"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.4rem)",
              color: "var(--jt-ivory)",
              lineHeight: 1.08,
              fontWeight: 500,
              maxWidth: "22ch",
              margin: "32px auto 0",
            }}
          >
            <span className="font-editorial italic" style={{ color: "var(--jt-bone)" }}>Request</span>
            <br />
            Membership
          </h1>

          <div className="gold-divider mx-auto mt-10" />

          <p
            className="font-editorial italic mt-10"
            style={{
              fontSize: "clamp(1.1rem, 1.6vw, 1.4rem)",
              color: "var(--jt-bone)",
              lineHeight: 1.55,
              maxWidth: "44ch",
              margin: "0 auto",
            }}
          >
            Private membership for verified flight attendants who believe layovers should be experienced, not endured.
          </p>

          <p
            className="mt-8"
            style={{
              color: "var(--jt-silver)",
              fontSize: "15px",
              lineHeight: 1.7,
              maxWidth: "52ch",
              margin: "0 auto",
            }}
          >
            Every request is personally reviewed. Selected applicants may be invited into the
            <span style={{ color: "var(--jt-gold)" }}> Founding Class of 2026</span> — a group of 225 flight
            attendants who help shape the cities, the standards, and the rooms that follow.
          </p>

          <div
            className="mt-10 inline-flex items-center gap-3"
            style={{ color: "var(--jt-gold)", fontSize: "13px" }}
          >
            <Clock size={14} />
            <span className="font-editorial italic" style={{ fontSize: "16px", color: "var(--jt-bone)" }}>
              Membership review takes approximately 60 seconds.
            </span>
          </div>
        </div>
      </section>

      <section className="px-6 pb-32">
        <form
          onSubmit={onSubmit}
          className="max-w-[760px] mx-auto"
          data-testid="apply-form"
          style={{
            background: "linear-gradient(180deg, #0A0A0A 0%, #050505 100%)",
            border: "1px solid rgba(201, 162, 75, 0.22)",
            borderRadius: 6,
            padding: "48px clamp(24px, 5vw, 56px) 56px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "radial-gradient(circle at 50% 0%, rgba(201,162,75,0.08) 0%, transparent 50%)",
              pointerEvents: "none",
            }}
          />

          <div className="relative">
            <div className="text-center">
              <div
                className="font-label"
                style={{ color: "var(--jt-gold)", fontSize: "12px" }}
              >
                REQUEST MEMBERSHIP
              </div>
              <div className="gold-divider mx-auto mt-5" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
              <Field label="First Name" required>
                <input
                  className="jt-input"
                  value={form.first_name}
                  onChange={update("first_name")}
                  data-testid="apply-first-name"
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  className="jt-input"
                  value={form.last_name}
                  onChange={update("last_name")}
                  data-testid="apply-last-name"
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Email Address" required>
                <input
                  type="email"
                  className="jt-input"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="apply-email"
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Airline" required>
                <input
                  className="jt-input"
                  value={form.airline}
                  onChange={update("airline")}
                  placeholder="e.g. Delta"
                  data-testid="apply-airline"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
              <Field label="Base Airport" required>
                <input
                  className="jt-input"
                  value={form.base_airport}
                  onChange={(e) => setForm((f) => ({ ...f, base_airport: e.target.value.toUpperCase() }))}
                  placeholder="BOS"
                  maxLength={6}
                  data-testid="apply-base-airport"
                />
              </Field>
              <Field label="Years Flying" required>
                <input
                  type="number"
                  min="0"
                  max="60"
                  className="jt-input"
                  value={form.years_flying}
                  onChange={update("years_flying")}
                  placeholder="0"
                  data-testid="apply-years-flying"
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Instagram Handle">
                <input
                  className="jt-input"
                  value={form.instagram_handle}
                  onChange={update("instagram_handle")}
                  placeholder="@yourhandle"
                  data-testid="apply-instagram"
                />
              </Field>
            </div>

            {/* Optional section divider */}
            <div className="flex items-center gap-4 mt-12 mb-6">
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: "linear-gradient(to right, transparent, var(--jt-gold))",
                  opacity: 0.5,
                }}
              />
              <span
                className="font-label"
                style={{ color: "var(--jt-gold)", fontSize: "10px" }}
              >
                OPTIONAL
              </span>
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: "linear-gradient(to left, transparent, var(--jt-gold))",
                  opacity: 0.5,
                }}
              />
            </div>

            <div>
              <Field label="Favorite Layover City">
                <input
                  className="jt-input"
                  value={form.favorite_layover_city}
                  onChange={update("favorite_layover_city")}
                  placeholder="LAX"
                  data-testid="apply-favorite-city"
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field
                label="What would bring you through the lounge doors?"
                labelColor="var(--jt-gold)"
              >
                <textarea
                  className="jt-textarea"
                  value={form.lounge_doors_response}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, 600);
                    setForm((f) => ({ ...f, lounge_doors_response: v }));
                  }}
                  data-testid="apply-lounge-doors"
                />
                <div
                  className="font-label text-right mt-2"
                  style={{ color: "var(--jt-mute)", fontSize: "10px" }}
                >
                  {form.lounge_doors_response.length}/600
                </div>
              </Field>
            </div>

            <div className="mt-6">
              <div
                className="font-label mb-4"
                style={{ color: "var(--jt-gold)", fontSize: "11px" }}
              >
                HOW DID YOU HEAR ABOUT JUMPSEAT THERAPY?
              </div>
              <div className="flex flex-wrap gap-3" data-testid="apply-referral-group">
                {REFERRAL_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    className="jt-chip"
                    data-selected={form.referral_source === opt.value}
                    onClick={() => setReferral(opt.value)}
                    data-testid={`apply-referral-${opt.label.replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    <span className="jt-chip-dot" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Invite Code (Optional) — auto-fills from ?ref= */}
            <div className="mt-10" data-testid="apply-invite-block">
              <div
                className="font-label mb-3"
                style={{ color: "var(--jt-gold)", fontSize: "11px" }}
              >
                INVITED BY A CHARTER MEMBER?
              </div>
              <Field label="Invite Code (Optional)">
                <input
                  className="jt-input"
                  value={form.invite_code}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().slice(0, 32);
                    setForm((f) => ({ ...f, invite_code: v }));
                  }}
                  placeholder="Example: HUNTER001"
                  autoComplete="off"
                  data-testid="apply-invite-code"
                />
                <p
                  className="font-editorial italic mt-3"
                  style={{ color: "var(--jt-silver)", fontSize: "14px", lineHeight: 1.55 }}
                >
                  If a Charter Member invited you, enter the invite code they shared with you.
                </p>
              </Field>
            </div>

            {/* Verification call section — method + contact required, availability optional */}
            <div className="mt-10" data-testid="apply-call-method-block">
              <div
                className="font-label mb-3"
                style={{ color: "var(--jt-gold)", fontSize: "11px" }}
              >
                BEST WAY TO REACH YOU FOR YOUR VERIFICATION CALL
              </div>
              <p
                style={{ color: "var(--jt-silver)", fontSize: "15px", lineHeight: 1.6, marginBottom: 14 }}
              >
                How should The Concierge reach you for your brief verification call?
              </p>
              <div
                className="flex flex-wrap gap-3"
                data-testid="apply-call-method-group"
                role="radiogroup"
                aria-label="Best way to reach you for your verification call"
              >
                {CALL_METHODS.map((m) => (
                  <button
                    type="button"
                    key={m.value}
                    className="jt-chip"
                    role="radio"
                    aria-checked={form.preferred_call_method === m.value}
                    data-selected={form.preferred_call_method === m.value}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        preferred_call_method: m.value,
                        // Clear the contact when switching methods to avoid stale mismatches
                        verification_call_contact:
                          f.preferred_call_method === m.value ? f.verification_call_contact : "",
                      }))
                    }
                    data-testid={`apply-call-method-${m.value.replace(/_/g, "-")}`}
                  >
                    <span className="jt-chip-dot" />
                    {m.label}
                  </button>
                ))}
              </div>

              {form.preferred_call_method && (
                <div className="mt-6" data-testid="apply-call-contact-block">
                  <Field label="Contact Information">
                    <input
                      className="jt-input"
                      value={form.verification_call_contact}
                      onChange={update("verification_call_contact")}
                      placeholder={
                        CALL_METHODS.find((x) => x.value === form.preferred_call_method)?.placeholder || ""
                      }
                      autoComplete="off"
                      data-testid="apply-call-contact-input"
                    />
                    <p
                      className="font-editorial italic mt-3"
                      style={{ color: "var(--jt-silver)", fontSize: "14px", lineHeight: 1.55 }}
                    >
                      Only provide the contact information needed for your selected verification method.
                      The Concierge will use this to coordinate your brief membership verification call.
                    </p>
                  </Field>
                </div>
              )}

              {/* Best times — optional */}
              <div className="mt-8" data-testid="apply-call-availability-block">
                <div
                  className="font-label mb-3"
                  style={{ color: "var(--jt-gold)", fontSize: "11px" }}
                >
                  BEST TIMES TO REACH YOU
                </div>
                <p
                  style={{ color: "var(--jt-silver)", fontSize: "15px", lineHeight: 1.6, marginBottom: 14 }}
                >
                  When are you generally available for a brief verification call?
                </p>
                <Field label="Availability + Time Zone (Optional)">
                  <textarea
                    className="jt-textarea"
                    value={form.verification_call_availability}
                    onChange={(e) => {
                      const v = e.target.value.slice(0, 500);
                      setForm((f) => ({ ...f, verification_call_availability: v }));
                    }}
                    placeholder="Example: Weekdays after 6 PM ET, or weekends after 2 PM ET"
                    style={{ minHeight: 96 }}
                    data-testid="apply-call-availability-input"
                  />
                  <div
                    className="font-label text-right mt-2"
                    style={{ color: "var(--jt-mute)", fontSize: "10px" }}
                  >
                    {form.verification_call_availability.length}/500
                  </div>
                </Field>
              </div>
            </div>

            <div className="mt-12 space-y-6 text-center">
              <p
                className="font-editorial italic"
                style={{ color: "var(--jt-silver)", fontSize: "16px", lineHeight: 1.55, maxWidth: "52ch", margin: "0 auto" }}
              >
                Every request is personally reviewed. Membership is granted by invitation only.
              </p>
              <p
                className="font-editorial italic"
                style={{ color: "var(--jt-gold)", fontSize: "16px", lineHeight: 1.55, maxWidth: "52ch", margin: "0 auto" }}
              >
                A select few will be invited into the Founding Class of 2026 — permanent recognition as part of the inaugural 225.
              </p>
            </div>

            {error && (
              <div
                data-testid="apply-error"
                className="mt-8 text-center font-editorial italic"
                style={{ color: "#D87E7E", fontSize: "15px" }}
              >
                {error}
              </div>
            )}

            <div className="mt-10">
              <button
                type="submit"
                className="jt-cta w-full"
                disabled={submitting}
                data-testid="apply-submit"
              >
                {submitting ? "Submitting…" : "Request Membership"}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/"
                className="font-editorial italic inline-flex items-center gap-2"
                style={{ color: "var(--jt-gold)", fontSize: "16px" }}
                data-testid="apply-back-home"
              >
                Return to the lounge <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </form>

        <CharterCitiesStrip />
      </section>

      <SiteFooter />
    </div>
  );
}

const Field = ({ label, required, children, labelColor }) => (
  <label className="block">
    <span
      className="font-label block mb-2"
      style={{ color: labelColor || "var(--jt-silver)", fontSize: "10px" }}
    >
      {label}{required ? "" : ""}
    </span>
    {children}
  </label>
);

const CharterCitiesStrip = () => (
  <div className="max-w-[820px] mx-auto mt-20 text-center">
    <div
      className="font-label"
      style={{ color: "var(--jt-gold)", fontSize: "11px", lineHeight: 1.6 }}
    >
      CHARTER CLASS 2026<br />
      FLIGHT ATTENDANTS FROM MAJOR AIRLINES<br />
      AND BASES ACROSS THE UNITED STATES
    </div>
    <div className="gold-divider mx-auto mt-6" />
    <div className="mt-10 flex flex-wrap justify-center gap-3">
      {CHARTER_CITIES.map((city) => (
        <span key={city} className="jt-city-pill">{city}</span>
      ))}
    </div>
  </div>
);
